import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { safeLocalStorage } from "@/lib/safeStorage";
import { AlertCircle, Menu, X, Home, Eye, EyeOff, Copy, Check, Clock, LayoutGrid, Building2, RefreshCw } from "lucide-react";
import {
  Receipt, FileText, CalendarClock, FileCheck2, ShieldCheck, Activity, Landmark, MessageCircle, Download,
  ArrowLeft, ArrowRight, ChevronRight, LogOut, Lock, Mail, ChevronDown, Inbox, Loader2, CheckCircle2,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/area-cliente`;
const MOD = {
  das: [Receipt, "Guias do mês e o que já foi pago."],
  dctf: [FileText, "DARFs da DCTFWeb para pagar."],
  parc: [CalendarClock, "Parcelas prontas para pagar."],
  comprovantes: [FileCheck2, "Pagamentos feitos à Receita."],
  cert: [ShieldCheck, "Situação e validade."],
  sitfis: [Activity, "Relatório da Receita Federal."],
  defis: [Landmark, "Recibos de entrega."],
};
const ROTULO_DATA = { das: "Vence em", dctf: "Vence em", parc: "Vence em", comprovantes: "Pago em", cert: "Válida até", sitfis: "Gerado em", defis: "Transmitida em" };
const brl = (v) => (typeof v === "number" ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "");
const data = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? new Date(String(v).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-BR") : v || "");
const comp = (v) => (/^\d{4}-\d{2}$/.test(String(v)) ? `${String(v).slice(5)}/${String(v).slice(0, 4)}` : v);
const fmtCnpj = (c) => { const d = String(c || "").replace(/\D/g, ""); return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : c; };
const titulo = (mod, it) => ({
  das: `Competência ${comp(it.titulo)}`, dctf: `DARF · competência ${comp(it.titulo)}`, parc: it.titulo || "Parcela",
  sitfis: "Relatório de situação fiscal", defis: `DEFIS ${it.titulo || ""}`,
}[mod] || it.titulo || "Documento");
const pill = (s) => {
  const t = String(s || "").toLowerCase();
  if (!t) return null;
  if (/vencid|atraso|irregular|positiva/.test(t)) return "bg-red-50 text-red-700 ring-red-200";
  if (/pago|quitad|regular|negativa|entregue|transmitid/.test(t)) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  return "bg-[#F0EDFF] text-[#2D1B96] ring-[#DCD5FF]";
};
const erroMsg = (e) => e?.response?.data?.detail || "Não foi possível concluir. Tente de novo.";
const iniciaisDe = (n) => (n || "").split(/\s+/).filter((p) => p.length > 2).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

function Marca({ esc, cor, claro }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {esc.logo ? <img src={esc.logo} alt="" className="h-10 w-10 rounded-xl object-contain bg-white p-1" />
        : <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-extrabold text-sm shrink-0" style={{ background: cor }}>{iniciaisDe(esc.nome)}</div>}
      <div className="min-w-0">
        <div className={`font-bold truncate ${claro ? "text-white" : "text-[#171744]"}`}>{esc.nome}</div>
        <div className={`text-xs ${claro ? "text-[#C9C6EA]" : "text-[#7E7B9A]"}`}>Área do cliente</div>
      </div>
    </div>
  );
}

const INSTAGRAM = "https://www.instagram.com/sousimpler/";
function IconeInstagram({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function AreaCliente() {
  const { slug } = useParams();
  const chave = `area_cliente_${slug}`;
  const [esc, setEsc] = useState(null);
  const [token, setToken] = useState(() => safeLocalStorage.getItem(chave));
  const [me, setMe] = useState(null);
  const [empId, setEmpId] = useState(null);
  const [resumo, setResumo] = useState({});
  const [mod, setMod] = useState(null);
  const [abrindo, setAbrindo] = useState(null);
  const [pend, setPend] = useState(null);
  const [menu, setMenu] = useState(false);
  const [aba, setAba] = useState("inicio");
  const [ocultar, setOcultar] = useState(() => safeLocalStorage.getItem("area_cliente_ocultar") === "1");
  const [copiado, setCopiado] = useState(null);
  const [painel, setPainel] = useState(null);
  const [gerando, setGerando] = useState(null);
  const [aviso, setAviso] = useState("");
  const [login, setLogin] = useState("");
  const [codigo, setCodigo] = useState("");
  const [etapa, setEtapa] = useState("login");
  const [msg, setMsg] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const cor = esc?.cor || "#5E49D7";
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const root = document.documentElement;
    const tinhaDark = root.classList.contains("dark");
    const forcarClaro = () => {
      if (root.classList.contains("dark")) root.classList.remove("dark");
      if (!root.classList.contains("light")) root.classList.add("light");
    };
    forcarClaro();
    root.style.colorScheme = "light";
    let meta = document.querySelector('meta[name="color-scheme"]');
    const metaAntes = meta ? meta.getAttribute("content") : null;
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "color-scheme"); document.head.appendChild(meta); }
    meta.setAttribute("content", "light only");
    const obs = new MutationObserver(forcarClaro);
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => {
      obs.disconnect();
      root.style.colorScheme = "";
      if (metaAntes === null) meta.remove(); else meta.setAttribute("content", metaAntes);
      if (tinhaDark) { root.classList.remove("light"); root.classList.add("dark"); }
    };
  }, []);

  const sair = () => { safeLocalStorage.removeItem(chave); setToken(null); setMe(null); setMod(null); setEtapa("login"); setCodigo(""); };
  const pedir = async (fn) => {
    setOcupado(true); setMsg("");
    try { return await fn(); } catch (e) { if (e?.response?.status === 401) sair(); setMsg(erroMsg(e)); } finally { setOcupado(false); }
  };

  useEffect(() => { axios.get(`${API}/publico/${slug}`).then((r) => setEsc(r.data)).catch(() => setEsc(false)); }, [slug]);
  useEffect(() => {
    if (!token) return;
    pedir(async () => { const r = await axios.get(`${API}/me`, h); setMe(r.data); setEmpId((id) => id || r.data.empresas[0]?.empresa_id); });
  }, [token]);
  useEffect(() => {
    if (!token || !empId) return;
    setMod(null);
    axios.get(`${API}/empresas/${empId}/resumo`, h).then((r) => setResumo(r.data)).catch(() => setResumo({}));
    setPend(null);
    axios.get(`${API}/empresas/${empId}/pendencias`, h).then((r) => setPend(r.data.itens || [])).catch(() => setPend([]));
    setPainel(null);
    axios.get(`${API}/empresas/${empId}/painel`, h).then((r) => setPainel(r.data)).catch(() => setPainel({}));
  }, [empId, token]);

  const solicitar = () => pedir(async () => { await axios.post(`${API}/auth/solicitar`, { slug, login }); setEtapa("codigo"); });
  const verificar = () => pedir(async () => {
    const r = await axios.post(`${API}/auth/verificar`, { slug, login, codigo });
    safeLocalStorage.setItem(chave, r.data.token);
    setToken(r.data.token);
  });
  const abrir = async (m) => {
    setAbrindo(m.id);
    await pedir(async () => { const r = await axios.get(`${API}/empresas/${empId}/${m.id}`, h); setMod(r.data); window.scrollTo(0, 0); });
    setAbrindo(null);
  };

  if (esc === false) {
    return <div className="min-h-screen grid place-items-center p-6 text-center bg-[#FBFBFD] text-[#171744]">Endereço não encontrado. Confira o link com a sua contabilidade.</div>;
  }
  if (!esc) return <div className="min-h-screen bg-[#FBFBFD] grid place-items-center"><Loader2 className="animate-spin text-[#C9C6EA]" /></div>;

  const campo = "h-12 w-full border border-[#E9E8F2] rounded-xl px-4 text-base text-[#171744] bg-white placeholder:text-[#A9A6C2] focus:outline-none focus:ring-2 focus:border-transparent";

  // ====================== LOGIN ======================
  if (!token || !me) {
    const recursos = [
      [Receipt, "Guias do Simples", "DAS"],
      [FileText, "DCTFWeb", "DARFs"],
      [CalendarClock, "Parcelamentos", "Parcelas"],
      [FileCheck2, "Comprovantes", "Pagamentos"],
      [ShieldCheck, "Certidões", "Regularidade"],
    ];
    const inputCls = "h-[58px] lg:h-[clamp(46px,6vh,58px)] w-full border-0 border-l-[4px] border-[#6938ef] bg-[#f4f4f5] px-4 text-[15px] text-slate-900 outline-none transition-all placeholder:text-[#5e61c8] focus:bg-[#f0f0f8] focus:ring-2 focus:ring-[#6938ef]/15";
    const btnCls = "group inline-flex h-[48px] min-w-[170px] items-center justify-center gap-2 rounded-[9px] bg-gradient-to-r from-[#142bc9] via-[#2737dc] to-[#6b39ef] px-7 text-sm font-bold text-white shadow-[0_10px_28px_rgba(75,55,220,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(75,55,220,0.34)] disabled:pointer-events-none disabled:opacity-60";
    const Erro = () => (msg ? (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{msg}
      </div>
    ) : null);

    return (
      <div className="min-h-[100svh] bg-white lg:grid lg:h-[100svh] lg:grid-cols-[42%_58%] lg:overflow-y-auto">

        {/* LADO ESQUERDO: acesso */}
        <section className="relative flex min-h-[100svh] flex-col bg-white px-6 py-7 sm:px-10 lg:min-h-full lg:px-14 lg:py-[clamp(12px,2.6vh,28px)] xl:px-20">
          <div className="relative z-10 flex min-h-[64px] items-center lg:min-h-0">
            {esc.logo
              ? <img src={esc.logo} alt={`Logo ${esc.nome}`} className="max-h-14 max-w-[230px] object-contain object-left" />
              : <img src="/brand/simpler-logo-oficial.png" alt="Simpler" className="h-16 w-16 object-contain object-left lg:h-[clamp(44px,6.5vh,64px)] lg:w-[clamp(44px,6.5vh,64px)]" />}
          </div>

          <div className="flex flex-1 items-center py-10 lg:py-[clamp(8px,2.5vh,40px)]">
            <div className="mx-auto w-full max-w-[450px] lg:mx-0">
              <div className="mb-9 lg:mb-[clamp(14px,3.6vh,36px)]">
                <div className="mb-3 lg:mb-[clamp(6px,1.2vh,12px)] text-[12px] font-semibold uppercase tracking-[0.22em] text-[#4d4ed8]">Área do cliente</div>
                <h1 className="text-[36px] font-bold leading-none tracking-[-0.035em] text-[#101652] sm:text-[42px] lg:text-[clamp(32px,4.6vh,42px)]">
                  {etapa === "login" ? "ACESSAR" : "CÓDIGO"}
                </h1>
                <p className="mt-5 lg:mt-[clamp(8px,2vh,20px)] text-[18px] font-medium text-[#5658c8]">
                  {etapa === "login" ? "Bem-vindo(a)!" : "Quase lá!"}
                </p>
                <p className="mt-1 text-[15px] leading-6 text-slate-500">
                  {etapa === "login"
                    ? <>Entre para acessar a área exclusiva de <b className="font-semibold text-[#101652]">{esc.nome}</b>. Use o e-mail ou celular cadastrado na sua contabilidade.</>
                    : <>Enviamos um código de 6 dígitos para <b className="font-semibold text-[#101652]">{login}</b>. Ele vale por 10 minutos.</>}
                </p>
              </div>

              {etapa === "login" ? (
                <form onSubmit={(e) => { e.preventDefault(); solicitar(); }} className="space-y-4 lg:space-y-[clamp(8px,1.6vh,16px)]">
                  <label className="block">
                    <span className="sr-only">E-mail ou celular</span>
                    <input className={inputCls} value={login} onChange={(e) => setLogin(e.target.value)} placeholder="E-mail ou celular" autoComplete="username" autoFocus required />
                  </label>
                  <Erro />
                  <div className="pt-4 lg:pt-[clamp(4px,1.2vh,16px)]">
                    <button type="submit" disabled={ocupado || !login} className={btnCls}>
                      {ocupado ? "Enviando..." : "Receber código"}
                      {!ocupado && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); verificar(); }} className="space-y-5">
                  <label className="block">
                    <span className="sr-only">Código de 6 dígitos</span>
                    <input className={`${inputCls} !h-[64px] text-center font-mono text-2xl tracking-[0.3em] text-[#101652]`} inputMode="numeric" autoComplete="one-time-code"
                      maxLength={6} autoFocus value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} placeholder="000000" />
                  </label>
                  <Erro />
                  <button type="submit" disabled={ocupado || codigo.length !== 6} className={`${btnCls} w-full`}>
                    {ocupado ? "Verificando..." : "Entrar"}
                    {!ocupado && <ArrowRight className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => { setEtapa("login"); setCodigo(""); setMsg(""); }}
                    className="inline-flex w-full items-center justify-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-[#3438cc]">
                    <ArrowLeft className="h-3.5 w-3.5" /> Usar outro contato ou reenviar código
                  </button>
                </form>
              )}

              <div className="mt-10 border-t border-slate-100 pt-6 lg:mt-[clamp(14px,3.6vh,40px)] lg:pt-[clamp(10px,2.2vh,24px)]">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-slate-500">
                  <span>Siga-nos</span>
                  <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[#3438cc] transition-colors hover:text-[#6b39ef]">
                    <IconeInstagram className="h-4 w-4" /> @sousimpler
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 flex min-h-[40px] lg:min-h-[clamp(20px,3vh,40px)] items-end justify-between gap-5 text-[11px] text-slate-400">
            <span>Ambiente protegido pela SIMPLER</span>
            <span className="hidden sm:inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Dados protegidos conforme a LGPD</span>
          </div>
        </section>

        {/* LADO DIREITO: experiência Simpler */}
        <aside className="relative hidden min-h-screen overflow-hidden lg:flex"
          style={{ background: "linear-gradient(135deg, #12072f 0%, #21104f 25%, #35206f 50%, #4a2fa9 74%, #355af6 100%)" }}>
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-[250px] -top-[250px] h-[760px] w-[760px] rounded-full border-[125px] border-white/[0.035]" />
            <div className="absolute -bottom-[400px] left-[100px] h-[760px] w-[760px] rounded-full border-[120px] border-[#9b6cff]/15" />
            <div className="absolute -right-[260px] -top-[140px] h-[620px] w-[620px] rotate-[38deg] rounded-[120px] bg-[#6d4aff]/12" />
            <div className="absolute left-[23%] top-[-15%] h-[150%] w-[110px] rotate-[43deg] bg-gradient-to-b from-[#815cff]/30 via-[#5539d7]/20 to-transparent" />
            <div className="absolute left-[48%] top-[-30%] h-[160%] w-[80px] rotate-[43deg] bg-gradient-to-b from-white/[0.025] via-[#7c5cff]/12 to-transparent" />
            <div className="absolute bottom-[8%] right-[4%] h-[300px] w-[300px] rounded-full bg-[#765cff]/15 blur-[100px]" />
            <div className="absolute left-[15%] top-[30%] h-[240px] w-[240px] rounded-full bg-[#a35cff]/14 blur-[100px]" />
          </div>

          <div className="relative z-10 grid w-full grid-cols-1 items-center gap-[clamp(24px,4vh,40px)] px-10 py-[clamp(20px,4.5vh,48px)] xl:grid-cols-[0.92fr_1.08fr] xl:px-14 2xl:px-20">
            <div className="max-w-[490px]">
              <div className="mb-[clamp(16px,3.2vh,32px)] inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.055] px-4 py-2 backdrop-blur-md">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/[0.06]">
                  <img src="/brand/simpler-logo-oficial.png" alt="Simpler" className="h-8 w-8 object-contain" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">SIMPLER</span>
              </div>

              <h2 className="max-w-[520px] text-[clamp(30px,4.6vh,38px)] font-bold leading-[1.02] tracking-[-0.045em] text-white xl:text-[clamp(32px,5.2vh,44px)] 2xl:text-[clamp(34px,5.8vh,50px)]">
                Suas guias.<br />Seus documentos.<br /><span className="text-[#ae96ff]">Mais simples.</span>
              </h2>

              <p className="mt-[clamp(14px,2.8vh,28px)] max-w-[420px] text-[15px] leading-7 text-white/65 xl:text-base">
                A {esc.nome} usa a Simpler para deixar tudo da sua empresa organizado e disponível quando você precisar.
              </p>

              <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="group mt-[clamp(16px,3.4vh,36px)] inline-flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#5634d7] shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-transform group-hover:translate-x-1">
                  <IconeInstagram className="h-5 w-5" />
                </span>
                <span className="rounded-full bg-white px-6 py-3 text-sm font-bold text-[#5634d7] shadow-[0_10px_30px_rgba(0,0,0,0.15)]">
                  Siga-nos @sousimpler
                </span>
              </a>
            </div>

            <div className="w-full max-w-[570px] justify-self-end">
              <div className="relative overflow-hidden rounded-[34px] border border-[#8b6cff]/55 bg-[#1b1048]/68 p-[clamp(16px,2.6vh,24px)] shadow-[0_30px_80px_rgba(0,0,0,0.28)] backdrop-blur-xl xl:p-[clamp(16px,2.8vh,28px)]">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-[#805cff]/[0.12]" />
                <div className="relative">
                  <div className="mb-[clamp(12px,2.8vh,28px)] flex items-start justify-between gap-4 px-1">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b99fff]">Área do cliente</p>
                      <h3 className="mt-2 text-[25px] font-bold tracking-[-0.025em] text-white">Tudo em um só lugar.</h3>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] text-[#b9a5ff]">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                  </div>

                  <div className="space-y-[clamp(6px,1.2vh,12px)]">
                    {recursos.map(([Icon, nome, det]) => (
                      <div key={nome} className="group flex min-h-[clamp(50px,7vh,66px)] items-center justify-between gap-4 rounded-[18px] border border-[#765de4]/40 bg-[#1d124f]/78 px-4 shadow-[0_0_0_1px_rgba(145,110,255,0.08),0_8px_30px_rgba(25,10,75,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#a283ff]/65 hover:bg-[#281765]/90">
                        <div className="flex min-w-0 items-center gap-3.5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.055] text-[#beaaff]">
                            <Icon className="h-[18px] w-[18px]" />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[15px] font-semibold text-white">{nome}</div>
                            <div className="mt-0.5 text-[11px] text-white/45">Disponível para download</div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 rounded-full border border-[#8d70ef]/30 bg-[#795cff]/15 px-3 py-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-[#aa91ff]" />
                          <span className="text-[11px] font-semibold text-[#d0c4ff]">{det}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-[clamp(12px,2.8vh,28px)] flex items-center justify-between border-t border-white/[0.08] px-1 pt-[clamp(10px,2vh,20px)]">
                    <div className="text-[11px] text-white/40">Seu acesso direto à sua contabilidade</div>
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#b29cff]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8b6cff] shadow-[0_0_12px_rgba(139,108,255,0.9)]" /> Simpler
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  // ====================== DENTRO ======================
  const emp = me.empresas.find((e) => e.empresa_id === empId);
  const zap = esc.whatsapp ? `https://wa.me/${String(esc.whatsapp).replace(/\D/g, "").replace(/^(?!55)/, "55")}` : null;
  const contatoEsc = zap || (esc.email ? `mailto:${esc.email}` : null);
  const IconMod = mod ? (MOD[mod.modulo]?.[0] || FileText) : null;
  const NOMES = { das: "DAS", dctf: "DARF DCTFWeb", parc: "Parcela" };
  const CURTO = { das: "DAS", dctf: "DCTFWeb", parc: "Parcelas", comprovantes: "Comprovantes", cert: "Certidões", sitfis: "Situação", defis: "DEFIS" };
  const primeiroNome = me.pessoa.nome.split(" ")[0];
  const lista = pend || [];
  const venc = (it) => /vencid/i.test(String(it?.status));
  const nVencidas = lista.filter(venc).length;
  const totalPagar = lista.reduce((a, it) => a + (typeof it.valor === "number" ? it.valor : 0), 0);
  const proximo = lista[0];
  const outras = lista.slice(1);
  const card = "bg-white rounded-[22px] border border-[#ececf4] shadow-[0_1px_2px_rgba(16,22,82,0.04)]";
  const valor = (v) => (ocultar ? "R$ ••••" : brl(v));
  const alternarOcultar = () => { const n = !ocultar; setOcultar(n); safeLocalStorage.setItem("area_cliente_ocultar", n ? "1" : "0"); };
  const copiar = (txt, chave) => { navigator.clipboard.writeText(String(txt).replace(/\D/g, "")); setCopiado(chave); setTimeout(() => setCopiado(null), 1800); };
  const irInicio = () => { setMod(null); setAba("inicio"); window.scrollTo(0, 0); };
  const irDocs = () => { setMod(null); setAba("docs"); window.scrollTo(0, 0); };
  const pedirLink = (it) => {
    const txt = `Olá! Aqui é ${me.pessoa.nome}, da ${emp?.nome || ""}. Preciso da guia atualizada (${NOMES[it.modulo] || "guia"} ${titulo(it.modulo, it)}), que venceu em ${data(it.data)}.`;
    if (zap) return `${zap}?text=${encodeURIComponent(txt)}`;
    if (esc.email) return `mailto:${esc.email}?subject=${encodeURIComponent("Guia atualizada")}&body=${encodeURIComponent(txt)}`;
    return null;
  };
  const podeGerar = (it) => it?.modulo === "das" && emp?.guia_atualizada !== false;
  const gerarAtualizada = async (it) => {
    setGerando(it.titulo); setMsg("");
    try {
      await axios.post(`${API}/empresas/${empId}/das/${it.titulo}/atualizar`, {}, h);
      const r = await axios.get(`${API}/empresas/${empId}/pendencias`, h);
      setPend(r.data.itens || []);
      setAviso("Guia atualizada gerada. Já pode baixar ou copiar o código.");
      setTimeout(() => setAviso(""), 4000);
    } catch (e) {
      setMsg(erroMsg(e));
    } finally {
      setGerando(null);
    }
  };
  const fmtAtual = (v) => {
    if (!v) return null;
    const d = new Date(String(v).replace(" ", "T"));
    if (isNaN(d)) return null;
    return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  };
  const atualizado = fmtAtual(painel?.atualizado_em);
  const certTxt = painel?.certidoes ? (painel.certidoes.total === 0 ? "Sem certidões" : painel.certidoes.irregulares ? `${painel.certidoes.irregulares} com pendência` : "Regulares") : null;
  const certOk = painel?.certidoes && painel.certidoes.total > 0 && !painel.certidoes.irregulares;
  const sitTxt = painel?.situacao ? (painel.situacao.pendencias ? `${painel.situacao.pendencias} ${painel.situacao.pendencias === 1 ? "pendência" : "pendências"}` : "Sem pendências") : null;
  const sitOk = painel?.situacao && !painel.situacao.pendencias;
  const modCert = emp?.modulos.find((m) => m.id === "cert");
  const modSit = emp?.modulos.find((m) => m.id === "sitfis");

  const chip = (Icon, rotulo, texto, ok, m) => (texto ? (
    <button key={rotulo} onClick={() => m && abrir(m)} className="flex items-center gap-2.5 rounded-[16px] bg-white/10 border border-white/10 px-3.5 py-2.5 text-left transition hover:bg-white/15 min-w-0">
      <span className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10"><Icon size={16} /></span>
      <span className="min-w-0 leading-tight">
        <span className="block text-[11px] font-normal text-white/60">{rotulo}</span>
        <span className="flex items-center gap-1.5 text-[13.5px] font-semibold">
          <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`} />{texto}
        </span>
      </span>
    </button>
  ) : null);

  const atalho = (m) => {
    const [Icon] = MOD[m.id] || [FileText];
    const n = resumo[m.id] || 0;
    return (
      <button key={m.id} onClick={() => abrir(m)} disabled={!!abrindo} className="group flex flex-col items-center gap-2 w-full">
        <span className="relative flex h-14 w-14 items-center justify-center rounded-[18px] bg-[#F3F1FF] text-[#2D1B96] transition-all group-hover:bg-[#2D1B96] group-hover:text-white group-active:scale-95">
          {abrindo === m.id ? <Loader2 size={22} className="animate-spin" /> : <Icon size={22} />}
          {n > 0 && <span className="absolute -right-1 -top-1 min-w-[20px] h-5 px-1 rounded-full bg-[#E5484D] text-white text-[10px] font-semibold grid place-items-center ring-2 ring-white">{n}</span>}
        </span>
        <span className="text-[12px] font-medium text-[#101652] text-center leading-tight">{CURTO[m.id] || m.nome}</span>
      </button>
    );
  };

  const linha = (modulo, it, i) => {
    const [Icon] = MOD[modulo] || [FileText];
    const v = venc(it);
    const pedir = v ? pedirLink(it) : null;
    const gera = v && podeGerar(it);
    const p = !v ? pill(it.status) : null;
    return (
      <div key={i} className="flex items-center gap-3 px-4 py-3.5 md:px-5">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] ${v ? "bg-red-50 text-red-600" : "bg-[#F3F1FF] text-[#2D1B96]"}`}><Icon size={19} /></span>
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-semibold text-[#101652] truncate">{titulo(modulo, it)}</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] font-normal">
            {it.data && <span className={v ? "text-red-600" : "text-slate-500"}>{v ? "Venceu em" : (ROTULO_DATA[modulo] || "Data")} {data(it.data)}</span>}
            {p && <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ring-1 ${p}`}>{it.status}</span>}
            {typeof it.valor === "number" && <span className={`sm:hidden basis-full font-semibold tabular-nums ${v ? "text-red-600" : "text-[#101652]"}`}>{valor(it.valor)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {typeof it.valor === "number" && <span className={`hidden sm:inline text-[14.5px] font-semibold tabular-nums mr-1 ${v ? "text-red-600" : "text-[#101652]"}`}>{valor(it.valor)}</span>}
          {gera ? (
            <button onClick={() => gerarAtualizada(it)} disabled={!!gerando} aria-label="Gerar guia atualizada" title="Gerar guia atualizada"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2D1B96] text-white transition hover:bg-[#24157a] active:scale-95 disabled:opacity-70">
              {gerando === it.titulo ? <Loader2 size={17} className="animate-spin" /> : <RefreshCw size={17} />}
            </button>
          ) : pedir && (
            <a href={pedir} target="_blank" rel="noreferrer" aria-label="Pedir guia atualizada" title="Pedir guia atualizada"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2D1B96] text-white transition hover:bg-[#24157a] active:scale-95"><MessageCircle size={17} /></a>
          )}
          {!v && it.codigo && (
            <button onClick={() => copiar(it.codigo, `l-${i}`)} aria-label="Copiar código de barras" title="Copiar código de barras"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e1ef] text-[#2D1B96] transition hover:bg-[#F3F1FF] active:scale-95">
              {copiado === `l-${i}` ? <Check size={16} /> : <Copy size={16} />}
            </button>
          )}
          {it.download && (
            <a href={`${BACKEND}${it.download}`} target="_blank" rel="noreferrer" aria-label={v ? "Baixar guia original" : "Baixar"} title={v ? "Baixar guia original (sem multa e juros)" : "Baixar"}
              className={v
                ? "flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e1ef] text-slate-500 transition hover:bg-slate-50 active:scale-95"
                : "flex h-10 w-10 items-center justify-center rounded-full bg-[#2D1B96] text-white transition hover:bg-[#24157a] active:scale-95"}>
              <Download size={17} />
            </a>
          )}
        </div>
      </div>
    );
  };

  const tab = (ativo, Icon, nome, onClick) => (
    <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium ${ativo ? "text-[#2D1B96]" : "text-slate-400"}`}>
      <Icon size={21} strokeWidth={ativo ? 2.3 : 2} />{nome}
    </button>
  );

  const pv = proximo && venc(proximo);
  const pedirProx = pv ? pedirLink(proximo) : null;

  return (
    <div className="min-h-[100svh] bg-[#F4F4F8] text-[#101652] font-normal antialiased">
      {/* FAIXA DO TOPO */}
      <div className="text-white bg-[#2D1B96]">
        <div className="mx-auto max-w-[1080px] h-16 px-4 md:px-6 flex items-center justify-between gap-3">
          <button onClick={irInicio} className="flex items-center gap-2.5 min-w-0 text-left">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-white p-1">
              {esc.logo ? <img src={esc.logo} alt="" className="h-full w-full object-contain" /> : <img src="/brand/simpler-logo-oficial.png" alt="Simpler" className="h-full w-full object-contain" />}
            </span>
            <div className="min-w-0 leading-tight">
              <div className="text-[14px] font-semibold truncate">{esc.nome}</div>
              <div className="text-[11px] font-normal text-white/65">Área do cliente</div>
            </div>
          </button>
          <div className="flex items-center gap-1.5">
            <button onClick={sair} className="hidden md:inline-flex h-9 px-3 items-center gap-2 rounded-full text-[13px] font-medium text-white/80 hover:bg-white/10"><LogOut size={15} /> Sair</button>
            <div className="ml-1 w-9 h-9 rounded-full grid place-items-center text-[12px] font-semibold bg-white text-[#2D1B96]" title={me.pessoa.nome}>{iniciaisDe(me.pessoa.nome) || me.pessoa.nome[0]}</div>
          </div>
        </div>
        <div className="mx-auto max-w-[1080px] px-4 md:px-6 pt-1 pb-[72px] md:pb-[76px] flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0">
            <h1 className="text-[24px] md:text-[28px] font-semibold tracking-[-0.02em] leading-tight">Olá, {primeiroNome}</h1>
            {emp && (me.empresas.length > 1 ? (
              <div className="relative mt-2.5 inline-flex max-w-full">
                <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/70" />
                <select aria-label="Trocar empresa" value={empId || ""} onChange={(e) => { setEmpId(e.target.value); irInicio(); }}
                  className="appearance-none max-w-full h-9 rounded-full bg-white/10 border border-white/15 pl-9 pr-9 text-[13px] font-medium text-white outline-none hover:bg-white/15 focus:ring-2 focus:ring-white/30 truncate">
                  {me.empresas.map((e) => <option key={e.empresa_id} value={e.empresa_id} className="text-[#101652]">{e.nome} · {fmtCnpj(e.cnpj)}</option>)}
                </select>
                <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-white/70" />
              </div>
            ) : (
              <div className="mt-2.5 inline-flex max-w-full items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3.5 h-9 text-[13px]">
                <Building2 size={15} className="shrink-0 text-white/70" />
                <span className="font-medium truncate">{emp.nome}</span>
                <span className="hidden sm:inline whitespace-nowrap font-normal text-white/55">· CNPJ {fmtCnpj(emp.cnpj)}</span>
              </div>
            ))}
            {emp && <p className="sm:hidden mt-1.5 pl-1 text-[11.5px] font-normal text-white/55">CNPJ {fmtCnpj(emp.cnpj)}</p>}
          </div>
          {emp && !mod && (certTxt || sitTxt) && (
            <div className="grid grid-cols-2 gap-2 md:flex md:gap-2.5">
              {chip(ShieldCheck, "Certidões", certTxt, certOk, modCert)}
              {chip(Activity, "Situação fiscal", sitTxt, sitOk, modSit)}
            </div>
          )}
        </div>
      </div>

      <main className="relative mx-auto max-w-[1080px] px-4 md:px-6 -mt-14 md:-mt-[60px] pb-28 md:pb-16 space-y-5 md:space-y-6">
        {!emp && <div className={`${card} p-5 text-[14px] text-slate-500`}>Nenhuma empresa liberada para este acesso. Fale com a sua contabilidade.</div>}

        {emp && !mod && aba === "inicio" && (
          <div className="flex flex-col gap-4 md:gap-5 lg:grid lg:grid-cols-[1.35fr_1fr] lg:gap-6 lg:items-start">
            <div className="order-1 flex flex-col gap-4 md:gap-5 lg:col-start-1 lg:row-start-1">
              {/* SALDO */}
              <section className={`${card} p-5 md:p-6 shadow-[0_14px_40px_-20px_rgba(45,27,150,0.45)]`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[13.5px] font-medium text-slate-500">Total a pagar</div>
                  <button onClick={alternarOcultar} aria-label={ocultar ? "Mostrar valores" : "Esconder valores"} className="h-9 w-9 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100">{ocultar ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
                {pend === null
                  ? <div className="mt-2 h-10 w-44 rounded-lg bg-slate-100 animate-pulse" />
                  : <div className={`mt-1 text-[34px] md:text-[40px] font-semibold tracking-[-0.03em] tabular-nums leading-none ${nVencidas ? "text-red-600" : ""}`}>{valor(totalPagar)}</div>}
                {pend !== null && (lista.length
                  ? <div className="mt-2 text-[13px] font-normal text-slate-500">
                      {lista.length} {lista.length === 1 ? "guia em aberto" : "guias em aberto"}
                      {nVencidas > 0 && <span className="text-red-600"> · {nVencidas === lista.length ? (nVencidas === 1 ? "vencida" : "todas vencidas") : `${nVencidas} ${nVencidas === 1 ? "vencida" : "vencidas"}`}</span>}
                    </div>
                  : <div className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-700"><CheckCircle2 size={15} /> Tudo em dia, nenhuma guia em aberto</div>)}

                {proximo && (
                  <div className={`mt-5 rounded-[18px] p-4 ${pv ? "bg-red-50/70" : "bg-[#F6F4FF]"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5b49c9]">Próxima guia · {NOMES[proximo.modulo]}</div>
                        <div className="mt-0.5 text-[15px] font-semibold truncate">{titulo(proximo.modulo, proximo)}</div>
                        {proximo.data && (
                          <div className="mt-0.5 flex items-center gap-2 text-[12.5px] font-normal text-slate-500">
                            {pv ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">Vencida em {data(proximo.data)}</span> : <>Vence em {data(proximo.data)}</>}
                          </div>
                        )}
                      </div>
                      {lista.length > 1 && typeof proximo.valor === "number" && <div className={`text-[17px] font-semibold tabular-nums shrink-0 ${pv ? "text-red-600" : ""}`}>{valor(proximo.valor)}</div>}
                    </div>
                    {pv && <p className="mt-3 text-[12.5px] font-normal leading-5 text-slate-600">{podeGerar(proximo) ? "Esta guia venceu. Gere a guia atualizada, com multa e juros calculados pela Receita, para pagar hoje." : "Guias vencidas precisam ser recalculadas com multa e juros. Peça a guia atualizada para a sua contabilidade antes de pagar."}</p>}
                    <div className="mt-3.5 flex flex-col gap-2 sm:flex-row">
                      {pv ? (
                        <>
                          {podeGerar(proximo) ? (
                            <button onClick={() => gerarAtualizada(proximo)} disabled={!!gerando}
                              className="inline-flex h-11 w-full sm:w-auto sm:flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#2D1B96] text-[13.5px] font-semibold text-white transition hover:bg-[#24157a] active:scale-[0.98] disabled:opacity-70">
                              {gerando === proximo.titulo ? <><Loader2 size={16} className="animate-spin" /> Gerando guia...</> : <><RefreshCw size={16} /> Gerar guia atualizada</>}
                            </button>
                          ) : pedirProx && (
                            <a href={pedirProx} target="_blank" rel="noreferrer"
                              className="inline-flex h-11 w-full sm:w-auto sm:flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#2D1B96] text-[13.5px] font-semibold text-white transition hover:bg-[#24157a] active:scale-[0.98]">
                              <MessageCircle size={16} /> Pedir guia atualizada
                            </a>
                          )}
                          {podeGerar(proximo) && pedirProx && (
                            <a href={pedirProx} target="_blank" rel="noreferrer"
                              className="inline-flex h-11 items-center justify-center gap-2 rounded-[14px] border border-[#e3e1ef] bg-white px-4 text-[13px] font-medium text-slate-600 transition hover:bg-slate-50">
                              <MessageCircle size={15} /> Falar com a contabilidade
                            </a>
                          )}
                        </>
                      ) : (
                        <>
                          {proximo.download && (
                            <a href={`${BACKEND}${proximo.download}`} target="_blank" rel="noreferrer"
                              className="inline-flex h-11 w-full sm:w-auto sm:flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#2D1B96] text-[13.5px] font-semibold text-white transition hover:bg-[#24157a] active:scale-[0.98]">
                              <Download size={16} /> Baixar guia
                            </a>
                          )}
                          {proximo.codigo && (
                            <button onClick={() => copiar(proximo.codigo, "proximo")}
                              className="inline-flex h-11 w-full sm:w-auto sm:flex-1 items-center justify-center gap-2 rounded-[14px] border border-[#d9d5f2] bg-white text-[13.5px] font-semibold text-[#2D1B96] transition hover:bg-[#F3F1FF] active:scale-[0.98]">
                              {copiado === "proximo" ? <Check size={16} /> : <Copy size={16} />}{copiado === "proximo" ? "Copiado" : "Copiar código de barras"}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {atualizado && (
                  <div className="mt-4 flex items-center gap-1.5 text-[12px] font-normal text-slate-400">
                    <Clock size={13} /> Atualizado pela sua contabilidade em {atualizado}
                  </div>
                )}
              </section>

              {/* OUTRAS GUIAS (só com 2 ou mais) */}
              {outras.length > 0 && (
                <section className={`${card} overflow-hidden`}>
                  <div className="px-4 md:px-5 pt-4 pb-1"><h2 className="text-[15px] font-semibold">Outras guias em aberto</h2></div>
                  <div className="divide-y divide-[#f0eff6]">{outras.map((it, i) => linha(it.modulo, it, i))}</div>
                </section>
              )}
            </div>

            {/* COLUNA DIREITA */}
            <div className="order-2 flex flex-col gap-4 md:gap-5 lg:col-start-2 lg:row-start-1">
              <section className={`${card} p-4 md:p-5`}>
                <h2 className="px-1 pb-4 text-[15px] font-semibold">Acesso rápido</h2>
                <div className="grid grid-cols-4 gap-x-1 gap-y-4 md:gap-y-5">
                  {emp.modulos.map(atalho)}
                  <button onClick={irDocs} className="group flex flex-col items-center gap-2 w-full">
                    <span className="flex h-14 w-14 items-center justify-center rounded-[18px] border border-dashed border-[#cfc9ef] text-[#2D1B96] transition group-hover:bg-[#F3F1FF] group-active:scale-95"><LayoutGrid size={21} /></span>
                    <span className="text-[12px] font-medium text-[#101652]">Ver todos</span>
                  </button>
                </div>
              </section>

              {contatoEsc && (
                <a href={contatoEsc} target="_blank" rel="noreferrer" className={`${card} p-4 flex items-center gap-3 transition hover:border-[#c9bfff]`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#2D1B96] text-white"><MessageCircle size={19} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14.5px] font-semibold">Fale com a contabilidade</div>
                    <div className="text-[12.5px] font-normal text-slate-500">Tire dúvidas sobre guias e documentos</div>
                  </div>
                  <ChevronRight size={18} className="text-slate-300" />
                </a>
              )}
              <a href={INSTAGRAM} target="_blank" rel="noreferrer" className={`${card} p-4 flex items-center gap-3 transition hover:border-[#c9bfff]`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#F3F1FF] text-[#2D1B96]"><IconeInstagram className="h-5 w-5" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-[14.5px] font-semibold">Siga a Simpler</div>
                  <div className="text-[12.5px] font-normal text-slate-500">@sousimpler no Instagram</div>
                </div>
                <ChevronRight size={18} className="text-slate-300" />
              </a>
            </div>
          </div>
        )}

        {emp && !mod && aba === "docs" && (
          <section className={`${card} overflow-hidden`}>
            <div className="px-4 md:px-5 pt-4 pb-2"><h2 className="text-[17px] font-semibold">Documentos</h2></div>
            <div className="divide-y divide-[#f0eff6]">
              {emp.modulos.map((m) => {
                const [Icon, desc] = MOD[m.id] || [FileText, ""];
                const n = resumo[m.id] || 0;
                return (
                  <button key={m.id} onClick={() => abrir(m)} disabled={!!abrindo} className="w-full flex items-center gap-3 px-4 md:px-5 py-3.5 text-left transition hover:bg-[#fafafc]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#F3F1FF] text-[#2D1B96]">
                      {abrindo === m.id ? <Loader2 size={19} className="animate-spin" /> : <Icon size={19} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14.5px] font-semibold">{m.nome}</div>
                      <div className="text-[12.5px] font-normal text-slate-500 truncate">{desc}</div>
                    </div>
                    {n > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E5484D] text-white text-[11px] font-semibold grid place-items-center">{n}</span>}
                    <ChevronRight size={18} className="text-slate-300" />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {emp && mod && (
          <section className={`${card} overflow-hidden`}>
            <div className="flex items-center gap-3 px-4 md:px-5 py-4 border-b border-[#f0eff6]">
              <button onClick={() => setMod(null)} aria-label="Voltar" className="h-10 w-10 shrink-0 grid place-items-center rounded-full text-slate-500 hover:bg-slate-100"><ArrowLeft size={19} /></button>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#2D1B96] text-white"><IconMod size={20} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-[17px] font-semibold leading-tight">{mod.nome}</div>
                <div className="text-[12.5px] font-normal text-slate-500">{mod.itens.length} {mod.itens.length === 1 ? "documento" : "documentos"}</div>
              </div>
              <button onClick={alternarOcultar} aria-label={ocultar ? "Mostrar valores" : "Esconder valores"} className="h-9 w-9 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100">{ocultar ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            {mod.itens.length === 0 ? (
              <div className="p-8 text-center space-y-1.5">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#F3F1FF] text-[#2D1B96]"><Inbox size={21} /></div>
                <p className="text-[14.5px] font-semibold pt-1">Nada por aqui ainda</p>
                <p className="text-[13px] font-normal text-slate-500">Quando a sua contabilidade tiver algo novo, aparece aqui.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#f0eff6]">{mod.itens.map((it, i) => linha(mod.modulo, it, i))}</div>
            )}
          </section>
        )}

        {msg && (
          <div role="status" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{msg}
          </div>
        )}

        <p className="flex items-center justify-center gap-1.5 pt-4 text-[11.5px] md:text-[12px] font-normal text-slate-400">
          <Lock size={12} /> Ambiente protegido pela SIMPLER · LGPD
        </p>
      </main>

      {aviso && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-8 z-40 rounded-full bg-emerald-600 px-4 py-2.5 text-[13px] font-medium text-white shadow-lg">{aviso}</div>
      )}
      {copiado && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-24 md:bottom-8 z-40 rounded-full bg-[#101652] px-4 py-2.5 text-[13px] font-medium text-white shadow-lg">
          Código de barras copiado
        </div>
      )}

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-[#ececf4] pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-4 h-16">
          {tab(!mod && aba === "inicio", Home, "Início", irInicio)}
          {tab(!!mod || aba === "docs", FileText, "Documentos", irDocs)}
          {contatoEsc
            ? <a href={contatoEsc} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-slate-400"><MessageCircle size={21} />Ajuda</a>
            : <span />}
          {tab(false, LogOut, "Sair", sair)}
        </div>
      </nav>
    </div>
  );
}
